import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSelectedAccountInfo, useAuth } from '@ibk/shared-auth';
import { useContaDigital } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { getCurrentDateISO } from '@ibk/shared-utils';
import { Currency, TransactionSource } from '../../enums';
import { useBilletConsultation } from '../useBilletConsultation';
import { useGetBilletData } from '../useGetBilletData';
import { usePostPayBillet } from '../usePostPayBillet';
import { usePostPayBilletScheduled } from '../usePostPayBilletScheduled/usePostPayBilletScheduled';
export function usePaymentBillet(props) {
    const [datePayment, setDatePayment] = useState(getCurrentDateISO());
    const [showPin, setShowPin] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [scheduledBillet, setScheduledBillet] = useState(false);
    const [viewReceipt, setViewReceipt] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [reSchedule, setReSchedule] = useState(false);
    const [hasRefetchedContaDigital, setHasRefetchedContaDigital] = useState(false);
    const navigate = useNavigate();
    const { loading: loadingGetBillet, errorGetBilletMessage, billetInfo, getBilletInfo, setBilletInfo, setErrorGetBilletMessage, } = useGetBilletData();
    const { buildPayV2Body, buildScheduleBody } = useBilletConsultation(billetInfo);
    const { errorMessagePostPayment, loading: loadingPostPayment, billetInfo: billetInfoPostPayment, errorNoFundsAvailable: errorNoFundsAvailablePostPayment, setErrorNoFundsAvailable, setErrorMessagePostPayment, postPaymentBillet, postPaymentBankslipV2, } = usePostPayBillet();
    const { errorMessagePostPaymentScheduled, loading: loadingPostPaymentScheduled, billetInfoScheduled: billetInfoPostPaymentScheduled, errorNoFundsAvailableScheduled, setErrorNoFundsAvailableScheduled, setErrorMessagePostPaymentScheduled, postPaymentScheduleBillet, errorScheduleAlreadyExists, setErrorScheduleAlreadyExists, } = usePostPayBilletScheduled();
    const { refetch: contaDigitalRefetch } = useContaDigital();
    const error = useMemo(() => errorGetBilletMessage || errorMessagePostPayment || errorMessagePostPaymentScheduled, [errorGetBilletMessage, errorMessagePostPayment, errorMessagePostPaymentScheduled]);
    const loading = useMemo(() => loadingGetBillet || loadingPostPayment || loadingPostPaymentScheduled, [loadingGetBillet, loadingPostPayment, loadingPostPaymentScheduled]);
    const { data: auth } = useAuth();
    const { isFeatureFlagWhiteListed } = useFeatureFlag(auth?.personId);
    const paymentSucceeded = Boolean(billetInfoPostPayment || billetInfoPostPaymentScheduled);
    const paymentTransactionId = billetInfoPostPayment?.transactionId ?? billetInfoPostPaymentScheduled?.transactionId;
    const paymentCoreBankId = billetInfoPostPayment?.id ?? billetInfoPostPaymentScheduled?.id;
    const isOperator = useMemo(() => {
        if (billetInfoPostPayment) {
            return !billetInfoPostPayment.transactionId;
        }
        if (billetInfoPostPaymentScheduled) {
            return !billetInfoPostPaymentScheduled.transactionId;
        }
        return false;
    }, [billetInfoPostPayment, billetInfoPostPaymentScheduled]);
    useEffect(() => {
        if (errorMessagePostPayment || errorMessagePostPaymentScheduled) {
            setShowAlert(true);
        }
    }, [errorMessagePostPayment, errorMessagePostPaymentScheduled]);
    useEffect(() => {
        if (props.barcode) {
            setErrorGetBilletMessage(undefined);
        }
    }, [props.barcode, setErrorGetBilletMessage]);
    useEffect(() => {
        if (props.barcode && props.isAccountPayable && !billetInfo && !error) {
            setErrorGetBilletMessage(undefined);
            setHasRefetchedContaDigital(false);
            getBilletInfo(props.barcode);
        }
    }, [props.barcode, props.isAccountPayable, billetInfo, error, setErrorGetBilletMessage, getBilletInfo]);
    useEffect(() => {
        if (billetInfo && currentStepIndex === 0) {
            setCurrentStepIndex(1);
        }
    }, [billetInfo, currentStepIndex]);
    useEffect(() => {
        if (paymentSucceeded && !hasRefetchedContaDigital) {
            contaDigitalRefetch();
            setHasRefetchedContaDigital(true);
        }
    }, [paymentSucceeded, contaDigitalRefetch, hasRefetchedContaDigital]);
    async function postBillet(pin) {
        if (!pin || !billetInfo) {
            return;
        }
        const operator = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAYMENTS_OPERATOR_REQUEST_PAYMENT);
        setShowPin(false);
        if (datePayment === getCurrentDateISO()) {
            setScheduledBillet(false);
            if (billetInfo.variant === 'v2') {
                postPaymentBankslipV2(buildPayV2Body(billetInfo, {
                    pin,
                    accountPayablesId: props.accountPayableId ?? undefined,
                }), operator);
                return;
            }
            const body = {
                credential: pin,
                amount: billetInfo.billet.amount,
                barCode: billetInfo.billet.barCode,
                digitableLine: billetInfo.billet.digitableLine,
                dueDate: billetInfo.billet.dueDate,
                accountPayablesId: props.accountPayableId ?? undefined,
                accountId: getSelectedAccountInfo()?.accountId ?? undefined,
                currency: Currency.BRL,
                source: TransactionSource.FINTECH_IBK,
            };
            postPaymentBillet(body, operator);
            return;
        }
        setScheduledBillet(true);
        postPaymentScheduleBillet(buildScheduleBody(billetInfo, {
            pin,
            paymentDate: datePayment,
            reSchedule,
            accountPayablesId: props.accountPayableId ?? undefined,
        }), operator);
    }
    function setStep(step) {
        if (step === 0) {
            setBilletInfo(undefined);
            setErrorMessagePostPayment(undefined);
            setErrorMessagePostPaymentScheduled(undefined);
            setErrorNoFundsAvailableScheduled(false);
            setErrorNoFundsAvailable(false);
            setErrorGetBilletMessage(undefined);
            setCurrentStepIndex(step);
            setDatePayment(getCurrentDateISO());
            setReSchedule(false);
        }
    }
    function handleGoHome() {
        if (props.isAccountPayable) {
            navigate(props.returnTo ?? '/pagamentos');
        }
        else {
            navigate('/conta-pj');
        }
    }
    function handleGoToNewPayment() {
        setHasRefetchedContaDigital(false);
        window.location.reload();
    }
    return {
        datePayment,
        error,
        loading,
        billetInfo,
        currentStepIndex,
        isOperator,
        paymentSucceeded,
        paymentTransactionId,
        paymentCoreBankId,
        errorNoFundsAvailablePostPayment,
        errorNoFundsAvailableScheduled,
        showPin,
        showAlert,
        scheduledBillet,
        viewReceipt,
        errorScheduleAlreadyExists,
        reSchedule,
        setErrorScheduleAlreadyExists,
        setShowPin,
        setShowAlert,
        setDatePayment,
        setErrorNoFundsAvailable,
        setErrorNoFundsAvailableScheduled,
        setCurrentStepIndex,
        getBilletInfo,
        postBillet,
        setStep,
        handleGoHome,
        handleGoToNewPayment,
        setViewReceipt,
        setReSchedule,
    };
}
